import React, { useState, useEffect } from 'react';
import Divider from '@mui/material/Divider';
import FormGroup from '@mui/material/FormGroup';
import TR from './../../Trans';
import Waiting from './../items/Waiting';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import InfoIcon from '@mui/icons-material/Info';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import ContentContainer from './../items/ContentContainer';
import Button from '@mui/material/Button';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import muvikaAttrs from './../../attrs';
import { useLocalStorage } from '../../services/useLocalStorage';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import settingsStore from '../../stores/settings';
import sessionStore from './../../stores/session';
import accountStore from '../../stores/account';
import performanceStore from '../../stores/performance';
import SmartQDialog from '../dialogs/SmartQSortDialog';
import navigationStore from './../../stores/navigation';
import { Views } from './../../enums/enums';
import NewSBCodeConfirmation from '../dialogs/NewSBCodeConfirmation';
import QRDialog from '../dialogs/QRDialog';
import Version from '../items/Version';
import SetPinDialog from '../dialogs/SetPinDialog';
import PinQuestion from '../dialogs/PinQuestion';
import InfoDialog from '../dialogs/InfoDialog';
import messageStore from './../../stores/message';

const pinAttemptTimeout = 60 * 1000;

function Settings() {

  const pinRef = React.useRef(null);
  const pinInputRef = React.useRef(null);

  const goToView = navigationStore((state) => state.goToView);
  const sessionToken = sessionStore((state) => state.sessionToken);
  const isSettingsSet = settingsStore((state) => state.isSet);
  const settings = settingsStore((state) => state.settings);
  const getSettings = settingsStore((state) => state.getSettings);
  const setSettings = settingsStore((state) => state.setSettings);
  const isAccountSet = accountStore((state) => state.isSet);
  const account = accountStore((state) => state.account);
  const getAccount = accountStore((state) => state.getAccount);
  const clearSmartQ = performanceStore((state) => state.clearSmartQ);
  const sortBySmartQ = performanceStore((state) => state.sortBySmartQ);
  const ackSmartQ = sessionStore((state) => state.ackSmartQ);
  const setError = messageStore((state) => state.setError);
  const isSaveable = settingsStore((state) => state.isSaveable);
  const setSaveable = settingsStore((state) => state.setSaveable);
  const setPaddingBottom = navigationStore((state) => state.setPaddingBottom);
  const adjustHistorySize = performanceStore((state) => state.adjustHistorySize);
  const setUnsubscriptionDialogShown = navigationStore((state) => state.setUnsubscriptionDialogShown);
  const setIsFirstRender = navigationStore((state) => state.setIsPlayerFirstRender);
  const urls = accountStore((state) => state.urls);

  const [isSmartQConfirmationOpen, setIsSmartQConfirmationOpen] = useState(false);
  const [isSbCodeConfirmationOpen, setIsSbCodeConfirmationOpen] = useState(false);
  const [isSbQROpen, setIsSbQROpen] = useState(false);
  const [isSettingsPinDialogOpen, setIsSettingsPinDialogOpen] = useState(false);
  const [isAccountPinDialogOpen, setIsAccountPinDialogOpen] = useState(false);
  const [isSettingsPinQuestionOpen, setIsSettingsPinQuestionOpen] = useState(true);
  const [isSettingsPinChecked, setIsSettingsPinChecked] = useState(false);
  const [isAccountPinQuestionOpen, setIsAccountPinQuestionOpen] = useState(false);
  const [isAccountPinChecked, setIsAccountPinChecked] = useState(false);

  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(false);
  const [autoPlayDelaySecs, setAutoPlayDelaySecs] = useState(0);
  const [numberNextSingersChange, setNumberNextSingersChange] = useState(0);
  const [isSongbookEnabled, setIsSongbookEnabled] = useState(false);
  const [isESongbookEnabled, setIsESongbookEnabled] = useState(false);
  const [isPlayNowDisabled, setIsPlayNowDisabled] = useState(false);
  const [sbCode, setSbCode] = useState(null);
  const [usageMode, setUsageMode] = useState(null);
  const [allowUserViewPlayList, setAllowUserViewPlayList] = useState(false);
  const [isSmartQEnabled, setIsSmartQEnabled] = useState(false);
  const [singerSelection, setSingerSelection] = useState("");
  const [historySize, setHistorySize] = useState(0);
  const [settingsPin, setSettingsPin] = useState(null);
  const [isSettingsPin, setIsSettingsPin] = useState(false);
  const [accountPin, setAccountPin] = useState(null);
  const [isAccountPin, setIsAccountPin] = useState(false);

  const [fontSize, setFontSize] = useLocalStorage(muvikaAttrs.service+"-font", 16);
  const [valFontSize, setValFontSize] = useState(fontSize);

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}px`;
  }, [fontSize]);

  const handleFontSelection = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    let fontSize = event.target.value;
    setValFontSize(fontSize);
  }

  const [wrongSettingsPinAttempts, setWrongSettingsPinAttempts] = useState(0);
  const [wrongAccountPinAttempts, setWrongAccountPinAttempts] = useState(0);

  const timeoutSettingsPinAttempts = () => {
    setTimeout(() => setWrongSettingsPinAttempts(0), pinAttemptTimeout);
  };

  const timeoutAccountPinAttempts = () => {
    setTimeout(() => setWrongAccountPinAttempts(0), pinAttemptTimeout);
  };

  const [isSmartQInfoDialogOpen, setIsSmartQInfoDialogOpen] = useState(false);

  const handleOpenSmartQInfoDialog = () => {
    setIsSmartQInfoDialogOpen(true);
  };

  const handleCloseSmartQInfoDialog = () => {
    setIsSmartQInfoDialogOpen(false);
  };

  const [isSongbookInfoDialogOpen, setIsSongbookInfoDialogOpen] = useState(false);

  const handleOpenSongbookInfoDialog = () => {
    setIsSongbookInfoDialogOpen(true);
  };

  const handleCloseSongbookInfoDialog = () => {
    setIsSongbookInfoDialogOpen(false);
  };

  const [sameSingerLimit, setSameSingerLimit] = useState(1);

  const handleOpenSmartQConfirmation = () => {
    setIsSmartQConfirmationOpen(true);
  };

  const handleCloseSmartQConfirmation = () => {
    setIsSmartQConfirmationOpen(false);
  };

  const handleOpenSbCodeConfirmation = () => {
    setIsSbCodeConfirmationOpen(true);
  };

  const handleCloseSbCodeConfirmation = () => {
    setIsSbCodeConfirmationOpen(false);
  };

  const handleOpenSettingsPinDialog = () => {
    setIsSettingsPinDialogOpen(true);
  };

  const handleCloseSettingsPinDialog = () => {
    setIsSettingsPinDialogOpen(false);
    setSettingsPin(null);
    setIsSettingsPin(false);
  };

  const handleOpenAccountPinDialog = () => {
    setIsAccountPinDialogOpen(true);
  };

  const handleCloseAccountPinDialog = () => {
    setIsAccountPinDialogOpen(false);
    setAccountPin(null);
    setIsAccountPin(false);
  };

  const handleOpenSettingsPinQuestion = () => {
    setIsSettingsPinQuestionOpen(true);
  };

  const handleCloseSettingsPinQuestion = () => {
    setIsSettingsPinQuestionOpen(false);
    goToView(Views.Home);
  };

  const handleOpenAccountPinQuestion = () => {
    setIsAccountPinQuestionOpen(true);
  };

  const handleCloseAccountPinQuestion = () => {
    setIsAccountPinQuestionOpen(false);
  };

  const handleOpenSbQR = () => {
    setIsSbQROpen(true);
  };

  const handleCloseSbQR = () => {
    setIsSbQROpen(false);
  };

  useEffect(() => {
    getSettings(sessionToken);
    if (!account) {
      getAccount(sessionToken);
    }
  }, []);

  useEffect(() => {
    if (settings) {
      setIsAutoPlayEnabled(settings.autoplay_enabled);
      setAutoPlayDelaySecs(settings.autoplay_secs);
      setNumberNextSingersChange(settings.num_singers_shown);
      setIsSongbookEnabled(settings.sb_enabled);
      setAllowUserViewPlayList(settings.sb_allow_playqueue_view);
      setIsSmartQEnabled(settings.smartq_enabled);
      setSingerSelection(settings.singer_selection);
      setHistorySize(settings.history_size);
      setIsESongbookEnabled(settings.esongbook_enabled);
      setIsPlayNowDisabled(settings.hide_play_now);
      setUsageMode(settings.usage_mode);
      setSettingsPin(settings.pin);
      setAccountPin(settings.account_pin);
      setIsSettingsPin(settings.pin ? true : false);
      setIsAccountPin(settings.account_pin ? true : false);
    }
  }, [settings]);

  const isCustomer = (tags) => {
    if (tags) {
      return tags.includes("consumer");
    }
    return false;
  };

  useEffect(() => {
    if (account && !isCustomer(account.tags)) {
      //console.log(settings.sb_same_singer_limit);
      setSameSingerLimit(settings.sb_same_singer_limit);
    }
  }, [account]);

  const saveSettings = () => {
    if ((settings.autoplay_enabled && !isAutoPlayEnabled) || (!settings.autoplay_enabled && isAutoPlayEnabled)) {
      setIsFirstRender(false);
    }
    let newSettings = { ...settings };
    newSettings.autoplay_enabled = isAutoPlayEnabled ? 1 : 0;
    newSettings.autoplay_secs = autoPlayDelaySecs;
    newSettings.sb_enabled = isSongbookEnabled ? 1 : 0;
    newSettings.sb_allow_playqueue_view = allowUserViewPlayList ? 1 : 0;
    newSettings.smartq_enabled = isSmartQEnabled ? 1 : 0;
    newSettings.singer_selection = singerSelection;
    newSettings.num_singers_shown = numberNextSingersChange;
    newSettings.history_size = historySize;
    if (account) {
      if (!isCustomer(account.tags)) {
        newSettings.sb_same_singer_limit = sameSingerLimit;
        newSettings.esongbook_enabled = isESongbookEnabled ? 1 : 0;
        newSettings.usage_mode = usageMode;
        newSettings.hide_play_now = isPlayNowDisabled ? 1 : 0;
        newSettings.pin = settingsPin;
        newSettings.account_pin = accountPin;
      }
    }
    if (isSmartQEnabled) {
      ackSmartQ();
      sortBySmartQ(sessionToken);
    } else {
      clearSmartQ(sessionToken);
    }
    setSaveable(false);
    setPaddingBottom("muvika-settings", 0);
    setUnsubscriptionDialogShown(true);
    document.documentElement.style.fontSize = `${valFontSize}px`;
    window.dispatchEvent(new Event('refitbuttons'));
    window.dispatchEvent(new Event('resize'));
    setSettings(sessionToken, newSettings);
    setFontSize(valFontSize)
    adjustHistorySize(sessionToken, historySize);
  };

  const handleEnableAutoPlay = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setIsAutoPlayEnabled(event.target.checked);
  };

  const handleAutoPlayDelaySecsChange = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setAutoPlayDelaySecs(event.target.value);
  };

  const handleNumberNextSingersChange = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setNumberNextSingersChange(event.target.value);
  };

  const handleEnableSongbook = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setIsSongbookEnabled(event.target.checked);
  };

  const handleAllowUserViewPlayList = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setAllowUserViewPlayList(event.target.checked);
  };

  const handleSingersLimitListChange = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setSameSingerLimit(event.target.value);
  };

  const handleEnableSmartQ = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    if (event.target.checked) {
      setIsSmartQConfirmationOpen(true);
    }
    setIsSmartQEnabled(event.target.checked);
  };

  const handleSingerSelection = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setSingerSelection(event.target.value);
  };

  const handleHistorySize = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setHistorySize(event.target.value);
  };

  const handleAllowESongBook = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setIsESongbookEnabled(event.target.checked);
  };

  const setNewSettingsPinFromDialog = (pin) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    if (pin.length > 0 && pin.length <= 32) {
      setSettingsPin(pin);
    } else {
      setSettingsPin(null);
      setIsSettingsPin(false);
    }
    setIsSettingsPinDialogOpen(false);
  };

  const setNewAccountPinFromDialog = (pin) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    if (pin.length > 0 && pin.length <= 32) {
      setAccountPin(pin);
    } else {
      setAccountPin(null);
      setIsAccountPin(false);
    }
    setIsAccountPinDialogOpen(false);
  };

  const handleEnableSettingsPin = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setIsSettingsPin(event.target.checked);
    if (event.target.checked) {
      handleOpenSettingsPinDialog(true);
    } else {
      setSettingsPin(null);
    }
  };

  const handleEnableAccountPin = (event) => {
    if (isAccountPin) {
      handleOpenAccountPinQuestion();
    } else {
      setSaveable(true);
      setPaddingBottom("muvika-settings", -45);
      setIsAccountPin(event.target.checked);
      if (event.target.checked) {
        handleOpenAccountPinDialog(true);
      } else {
        setAccountPin(null);
      }
    }
  };

  const handleDisablePlayNow = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setIsPlayNowDisabled(event.target.checked);
  };

  const handleUsageMode = (event) => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    setUsageMode(event.target.value);
  };

  const handleNewSbCode = () => {
    setSaveable(true);
    setPaddingBottom("muvika-settings", -45);
    let newSettings = { ...settings };
    newSettings.sb_code = "random";
    setSettings(sessionToken, newSettings);
    setIsSbCodeConfirmationOpen(false);
  };

  const handleCheckSettingsPin = (pin) => {
    if (pin === settings.pin) {
      setIsSettingsPinChecked(true);
    } else {
      if (wrongSettingsPinAttempts == 0) {
        timeoutSettingsPinAttempts();
      }
      if (wrongSettingsPinAttempts >= 4) {
        setError(sessionToken, 0, TR.cm_too_many_failed_attempts_try_later);
      } else {
        setError(sessionToken, 0, TR.cm_invalid_pin);
      }
      setWrongSettingsPinAttempts(wrongSettingsPinAttempts + 1);
    }
  };

  const handleCheckAccountPin = (pin) => {
    if (pin === settings.account_pin) {
      setIsAccountPinChecked(false);
      handleCloseAccountPinQuestion();
      setSaveable(true);
      setAccountPin(null);
      setPaddingBottom("muvika-settings", -45);
      setIsAccountPin(false);
    } else {
      if (wrongAccountPinAttempts == 0) {
        timeoutAccountPinAttempts();
      }
      if (wrongAccountPinAttempts >= 4) {
        setError(sessionToken, 0, TR.cm_too_many_failed_attempts_try_later);
      } else {
        setError(sessionToken, 0, TR.cm_invalid_pin);
      }
      setWrongAccountPinAttempts(wrongSettingsPinAttempts + 1);
    }
  };

  const autoPlayDelayList = [0, 5, 10, 20, 30, 45, 60, 120, 300].map((secs) =>
    <MenuItem key={secs} value={secs}>{secs}</MenuItem>
  );

  const singersList = [0, 1, 2, 3, 4, 5, 10, 20].map((singers) =>
    <MenuItem key={singers} value={singers}>{singers}</MenuItem>
  );

  const singersLimitList = [1, 2, 3, 4, 5].map((singers) =>
    <MenuItem key={singers} value={singers}>{singers}</MenuItem>
  );

  const videoForm = () => {
    return <div className="muvika-settings-group">
      <h4 className="muvika-settings-group-title">{TR.cm_video}</h4>
      <FormGroup >
        <Stack spacing={0}>
          <FormControlLabel className="muvika-form-component" onChange={handleEnableAutoPlay} control={<Checkbox checked={isAutoPlayEnabled} />} label={TR.cm_autoplay_next} />
          <Grid className="muvika-settings-grid" container spacing={0}>
            <Grid item xs={7}>
              <InputLabel className="muvika-settings-label">{TR.cm_autoplay_delay_secs}</InputLabel>
            </Grid>
            <Grid item xs={5}>
              <Select className="muvika-settings-select"
                defaultValue={settings.autoplay_secs}
                onChange={handleAutoPlayDelaySecsChange}
              >
                {autoPlayDelayList}
              </Select>
            </Grid>
          </Grid>
          <Grid className="muvika-settings-grid" container spacing={0}>
            <Grid item xs={7}>
              <InputLabel className="muvika-settings-label">{TR.cm_num_next_singers_shown}</InputLabel>
            </Grid>
            <Grid item xs={5}>
              <Select className="muvika-settings-select"
                defaultValue={settings.num_singers_shown}
                onChange={handleNumberNextSingersChange}
              >
                {singersList}
              </Select>
            </Grid>
          </Grid>
        </Stack>
      </FormGroup>
    </div>;
  };

  const songbookForm = () => {
    return <div className="muvika-settings-group">
      <h4 className="muvika-settings-group-title">
        {TR.cm_songbook}
        <IconButton className="muvika-settings-info-button" color="white" component="label" onClick={handleOpenSongbookInfoDialog}>
          <InfoIcon />
        </IconButton>
      </h4>
      {songbookFormBasic()}
      {songbookFormPro()}
    </div>;
  };

  const getPlainSongbookUrl = () => {
    if (urls) {
      let url = urls.url_songbook;
      let urlSplitted = url.split(".");
      if (urlSplitted.length > 2) {
        return urlSplitted[urlSplitted.length - 2] + "." + urlSplitted[urlSplitted.length - 1];
      } else {
        let urlSplitted = url.split("://");
        if (urlSplitted.length == 2) {
          return urlSplitted[1];
        }
      }
    }
    return "";
  };

  const getSongbookUrlWithCode = () => {
    if (urls) {
      let url = urls.url_songbook;
      return url + "?songbook_code=" + settings.sb_code;
    }
    return "";
  };

  const songbookCode = () => {
    if (!(account && !isCustomer(account.tags) && settings)) {
      return <Grid className="muvika-settings-grid" container spacing={0}>
        <Grid item xs={12}>
          <InputLabel style={{ whiteSpace: "unset !important" }} className="muvika-settings-label"><span>{getPlainSongbookUrl()} <b>{settings.sb_code}</b></span></InputLabel>
        </Grid>
      </Grid>;
    }
  };

  const songbookCodeWithNewButton = () => {
    if (account && !isCustomer(account.tags) && settings) {
      return <Grid className="muvika-settings-grid" container spacing={0}>
        <Grid item xs={7}>
          <InputLabel style={{ wordWrap: "break-word" }} className="muvika-settings-label"><span>{getPlainSongbookUrl()} <b>{settings.sb_code}</b></span></InputLabel>
        </Grid>
        <Grid item xs={5}>
          <FormControlLabel className="muvika-form-component" control={<Button onClick={handleOpenSbCodeConfirmation} variant="text" color="success">{TR.cm_recreate}</Button>} />
        </Grid>
      </Grid>;
    }
  };

  const songbookInfos = () => {
    if (isSongbookEnabled) {
      return <Stack>
        {songbookCode()}
        {songbookCodeWithNewButton()}
        <Stack spacing={0}>
          <FormControlLabel className="muvika-form-component" onChange={handleAllowUserViewPlayList} control={<Checkbox defaultChecked={settings.sb_allow_playqueue_view == 1 ? true : false} />} label={TR.cm_allow_playlist_view} />
        </Stack>
      </Stack>;
    }
  };

  const songbookFormBasic = () => {
    return <FormGroup>
      <Stack spacing={0}>
        <FormControlLabel className="muvika-form-component" onChange={handleEnableSongbook} control={<Checkbox checked={isSongbookEnabled} />} label={TR.cm_enable_songbook} />
      </Stack>
      {songbookInfos()}
    </FormGroup>;
  };

  const songbookFormPro = () => {
    if (account && !isCustomer(account.tags) && settings) {
      if (isSongbookEnabled) {
        return <div>
          <FormGroup>
            <Grid className="muvika-settings-grid" container spacing={0}>
              <Grid item xs={7}>
                <InputLabel className="muvika-settings-label">{TR.cm_max_songs_from_same_singer}</InputLabel>
              </Grid>
              <Grid item xs={5}>
                <Select
                  className="muvika-settings-select"
                  defaultValue={settings.sb_same_singer_limit}
                  onChange={handleSingersLimitListChange}
                >
                  {singersLimitList}
                </Select>
              </Grid>
            </Grid>
          </FormGroup>
          <FormGroup>
            <Grid className="muvika-settings-grid" container spacing={0}>
              <Grid item xs={7}>
                <InputLabel className="muvika-settings-label">{TR.cm_songbook_url_as_qrcode}</InputLabel>
              </Grid>
              <Grid item xs={5}>
                <FormControlLabel control={<Button onClick={handleOpenSbQR} variant="text" color="success">{TR.cm_show_qrcode}</Button>} />
              </Grid>
            </Grid>
          </FormGroup>
        </div>;
      }
    }
  };

  const smartqForm = () => {
    return <div className="muvika-settings-group">
      <h4 className="muvika-settings-group-title">
        SmartQ
        <IconButton className="muvika-settings-info-button" color="white" component="label" onClick={handleOpenSmartQInfoDialog}>
          <InfoIcon />
        </IconButton>
      </h4>

      <FormGroup>
        <Stack spacing={0}>
          <FormControlLabel className="muvika-form-first muvika-form-component" onChange={handleEnableSmartQ} control={<Checkbox checked={isSmartQEnabled} />} label={TR.cm_enable_smartq} />
        </Stack>
      </FormGroup>
    </div>;
  };

  const otherForm = () => {
    return <div className="muvika-settings-group">
      <h4 className="muvika-settings-group-title">{TR.cm_other_options}</h4>
      {otherFormBasic()}
      {otherFormPro()}
      {otherFormFont()}
    </div>;
  };

  const otherFormFont = () => {
    return <div>
      <FormGroup>
        <Grid className="muvika-settings-grid" container spacing={2}>
          <Grid item xs={7}>
            <InputLabel className="muvika-settings-label">{TR.cm_select_font}</InputLabel>
          </Grid>
          <Grid item xs={5}>
            <Select className="muvika-settings-select"
              onChange={handleFontSelection}
              defaultValue={fontSize}
            >
              {Array.from({ length: 13 }, (_, i) => {
                const value = i + 10;
                return (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                );
              })}
            </Select>
          </Grid>
        </Grid>
      </FormGroup>
    </div>;
  };


  const otherFormBasic = () => {
    return <div>
      <FormGroup>
        <Grid className="muvika-settings-grid" container spacing={2}>
          <Grid item xs={7}>
            <InputLabel className="muvika-settings-label">{TR.cm_select_singer}</InputLabel>
          </Grid>
          <Grid item xs={5}>
            <Select className="muvika-settings-select"
              onChange={handleSingerSelection}
              defaultValue={settings.singer_selection}
            >
              <MenuItem value={"list"}>{TR.cm_from_list}</MenuItem>
              <MenuItem value={"type"}>{TR.cm_by_typing}</MenuItem>
            </Select>
          </Grid>
        </Grid>
      </FormGroup>
      <FormGroup>
        <Grid className="muvika-settings-grid" container spacing={2}>
          <Grid item xs={7}>
            <InputLabel className="muvika-settings-label">{TR.cm_history_size}</InputLabel>
          </Grid>
          <Grid item xs={5}>
            <Select className="muvika-settings-select"
              onChange={handleHistorySize}
              defaultValue={settings.history_size}
            >
              <MenuItem value={0}>0</MenuItem>
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
            </Select>
          </Grid>
        </Grid>
      </FormGroup>
    </div>;
  };

  const otherFormPro = () => {
    if (account && !isCustomer(account.tags)) {
      return <div>
        <FormGroup>
          <Stack spacing={0}>
            <FormControlLabel className="muvika-form-component" onChange={handleEnableSettingsPin} control={<Checkbox checked={isSettingsPin} defaultChecked={settings.pin ? true : false} />} label={TR.cm_enable_settings_pin} />
          </Stack>
          <Stack spacing={0}>
            <FormControlLabel className="muvika-form-component" onChange={handleEnableAccountPin} control={<Checkbox checked={isAccountPin} defaultChecked={settings.account_pin ? true : false} />} label={TR.cm_enable_account_pin} />
          </Stack>
          <Stack spacing={0}>
            <FormControlLabel className="muvika-form-component" onChange={handleDisablePlayNow} control={<Checkbox defaultChecked={settings.hide_play_now == 1 ? true : false} />} label={TR.cm_hide_play_now} />
          </Stack>
        </FormGroup>
        <FormGroup>
          <Grid className="muvika-settings-grid" container spacing={0}>
            <Grid item xs={7}>
              <InputLabel className="muvika-settings-label">{TR.cm_usage_mode}</InputLabel>
            </Grid>
            <Grid item xs={5}>
              <Select className="muvika-settings-select"
                onChange={handleUsageMode}
                defaultValue={settings.usage_mode}
              >
                <MenuItem value={"restaurant"}>{TR.cm_restaurant}</MenuItem>
                <MenuItem value={"booth"}>{TR.cm_booth}</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </FormGroup>
      </div>;
    }
  };

  const settingsForm = () => {
    if (settings) {
      return <ContentContainer id="muvika-settings" onScrollDown={() => {
      }} isFinished={true}>
        {videoForm()}

        {songbookForm()}

        {smartqForm()}

        {otherForm()}
      </ContentContainer>;
    }
  };

  const waitingRender = () => {
    if (isSettingsSet) {
      return settingsForm();
    } else {
      return <Waiting />;
    }
  };

  const renderSaveButton = () => {
    if (isSaveable) {
      return <div className="muvika-save-div">
        <Button className="muvika-save-button" variant="text" color="success" onClick={
          saveSettings
        }>{TR.cm_save}
        </Button>
      </div>;
    }
  };

  const renderPinDialogOrForm = () => {
    if (settings) {
      if (settings.pin && !isSettingsPinChecked) {
        return <PinQuestion open={isSettingsPinQuestionOpen} onClose={handleCloseSettingsPinQuestion} onCheckPin={handleCheckSettingsPin} />;
      } else {
        return <div className="muvika-settings-container">
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <h3>{TR.cm_settings}</h3>
            </Grid>
          </Grid>
          {waitingRender()}
          {renderSaveButton()}
        </div>;
      }
    }
  };

  return (
    <div>
      <PinQuestion open={isAccountPinQuestionOpen} onClose={handleCloseAccountPinQuestion} onCheckPin={handleCheckAccountPin} />
      <InfoDialog open={isSongbookInfoDialogOpen} onClose={handleCloseSongbookInfoDialog} title={TR.cm_songbook} text={TR.cm_songbook_info} />
      <InfoDialog open={isSmartQInfoDialogOpen} onClose={handleCloseSmartQInfoDialog} title={"SmartQ"} text={TR.cm_smartq_info} />
      <QRDialog open={isSbQROpen} onClose={handleCloseSbQR} url={getSongbookUrlWithCode()} />
      <NewSBCodeConfirmation open={isSbCodeConfirmationOpen} onClose={handleCloseSbCodeConfirmation} onConfirmation={handleNewSbCode} />
      <SmartQDialog open={isSmartQConfirmationOpen} onClose={handleCloseSmartQConfirmation} />
      <SetPinDialog open={isSettingsPinDialogOpen} onClose={handleCloseSettingsPinDialog} onSetPin={setNewSettingsPinFromDialog} />
      <SetPinDialog open={isAccountPinDialogOpen} onClose={handleCloseAccountPinDialog} onSetPin={setNewAccountPinFromDialog} />
      {renderPinDialogOrForm()}
    </div>
  );
}

export default Settings;